import React, { useMemo } from 'react';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { AddCircle } from '@mui/icons-material';
import { matchesSearch } from 'utils/formatters';
import { CheckboxField, InputField, RadioGroupField } from 'components/form';
import { SubmitHandler, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';

import { Charge } from '../../types';
import { addValueFieldToCharges } from '../../lib/charges';
import { allCharges, ChargeDefinition, ChargeType, newChargeRow, sortByOptions } from '../../constants';

type AddChargeFormValue = {
  filters: { search: string; type: string };
  charges: Record<ChargeType, (ChargeDefinition & { value: boolean })[]>;
};

export const EquipmentChargesDialog = () => {
  const { isOpen, handleClose, data } = useModal(ModalsKeys.ChargesEquipmentDialog);

  const { control, getValues, handleSubmit } = useForm<AddChargeFormValue>({
    defaultValues: {
      filters: { search: '', type: 'all' },
      charges: addValueFieldToCharges(allCharges),
    },
  });

  const { fields: taxesAndFeesFields } = useFieldArray({ control, name: 'charges.taxesAndFees' });

  const filters = useWatch({ control, name: 'filters' });

  const filteredFields = useMemo(() => {
    const searchQuery = filters.search;

    return {
      taxesAndFees: taxesAndFeesFields.filter((_, index) =>
        matchesSearch(getValues(`charges.taxesAndFees.${index}.label`), searchQuery)
      ),
    };
  }, [filters.search, taxesAndFeesFields, getValues]);

  const hasSearchResults = useMemo(() => {
    if (!filters.search) return true;

    return [filteredFields.taxesAndFees.length].some(Boolean);
  }, [filters.search, filteredFields]);

  const onSubmit: SubmitHandler<AddChargeFormValue> = ({ charges }) => {
    const chargesToSet = Object.values(charges).reduce<Omit<Charge, 'attachTo'>[]>((acc, value) => {
      value
        .filter(({ value }) => Boolean(value))
        .forEach(({ type, label, category }) => {
          acc.push({ type: category, label, financeType: type, price: newChargeRow });
        });

      return acc;
    }, []);

    data.onConfirm(chargesToSet);
  };

  return (
    <Dialog fullWidth maxWidth="lg" open={isOpen} onClose={handleClose}>
      <DialogTitle>Add Equipment Charges</DialogTitle>

      <DialogContent dividers>
        <Stack gap="1rem">
          <Stack gap="1rem">
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem' }}>
              <InputField
                name="filters.search"
                control={control}
                inputProps={{ variant: 'outlined', placeholder: 'Search by Name or Type' }}
              />

              <Button onClick={data?.onOpenCreateChargeDialog} startIcon={<AddCircle />}>
                Create charge
              </Button>
            </Box>

            <RadioGroupField row name="filters.type" label="Sort by" control={control} options={sortByOptions} />
          </Stack>

          <Typography variant="subtitle2" component="span">
            Invoice Item
          </Typography>

          {!hasSearchResults && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No charges found matching "{filters.search}"
            </Typography>
          )}

          <RenderWithCondition
            condition={['all', 'taxesAndFees'].includes(filters.type) && filteredFields.taxesAndFees.length > 0}
          >
            <Stack gap="0.5rem">
              <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                Taxes and Fees {filters.search && `(${filteredFields.taxesAndFees.length})`}
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '1rem' }}>
                {filteredFields.taxesAndFees.map(field => {
                  const originalIndex = taxesAndFeesFields.findIndex(({ id }) => id === field.id);
                  return (
                    <CheckboxField
                      key={field.id}
                      control={control}
                      name={`charges.taxesAndFees.${originalIndex}.value`}
                      label={getValues(`charges.taxesAndFees.${originalIndex}.label`)}
                    />
                  );
                })}
              </Box>
            </Stack>
          </RenderWithCondition>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="contained" onClick={handleSubmit(onSubmit)}>
          Save
        </Button>

        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};
